import PropTypes from 'prop-types';
import TranslationUtil from '../../../../utils/TranslationUtil';
import JoinDetailsAction from '../../../../actions/joinDetails';
import { addressFields } from '../../../../utils/AddressUtil';
import { getUnitCountLabel } from './PropertyDetailsHelper';

const validationObjectProps = {
    isValid: PropTypes.bool.isRequired,
    error: PropTypes.string
};

const propertyDetailsProps = {
    referrer: PropTypes.string.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired
    }).isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func.isRequired
    }).isRequired,
    title: PropTypes.string.isRequired,
    propertyNameLabel: PropTypes.string.isRequired,
    propertyName: PropTypes.string.isRequired,
    latinPropertyName: PropTypes.string.isRequired,
    propertyNameValidation: PropTypes.shape(validationObjectProps).isRequired,
    latinPropertyNameValidation: PropTypes.shape(validationObjectProps).isRequired,
    propertyTypeValidation: PropTypes.shape(validationObjectProps).isRequired,
    propertyType: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    currencyValidation: PropTypes.shape(validationObjectProps).isRequired,
    managerInfo: PropTypes.string,
    chainInfo: PropTypes.string,
    setPropertyName: PropTypes.func.isRequired,
    setLatinPropertyName: PropTypes.func.isRequired,
    propertyTypeLabel: PropTypes.string.isRequired,
    setPropertyType: PropTypes.func.isRequired,
    setCurrency: PropTypes.func.isRequired,
    setUnitChange: PropTypes.func.isRequired,
    setManagerInfo: PropTypes.func.isRequired,
    setChainInfo: PropTypes.func.isRequired,
    roomsLabel: PropTypes.string.isRequired,
    unitCount: PropTypes.string.isRequired,
    unitCountValidation: PropTypes.shape(validationObjectProps).isRequired,
    currencyLabel: PropTypes.string.isRequired,
    managerInfoLabel: PropTypes.string.isRequired,
    chainInfoLabel: PropTypes.string.isRequired,
    yesLabel: PropTypes.string.isRequired,
    noLabel: PropTypes.string.isRequired,
    propertyNameToolTip1: PropTypes.string.isRequired,
    propertyNameToolTip2: PropTypes.string.isRequired,
    propertyNameToolTip3: PropTypes.string.isRequired,
    roomsToolTip: PropTypes.string.isRequired,
    managerToolTip1: PropTypes.string.isRequired,
    managerToolTip2: PropTypes.string.isRequired,
    chainInfoToolTip: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    fetchCurrencyOnCountryCode: PropTypes.func.isRequired,
    hydrationComplete: PropTypes.bool,
    latinRomanTip: PropTypes.string.isRequired,
    currencyList: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        values: PropTypes.array
    }).isRequired,
    analytics: PropTypes.shape({
        viewTTIstart: PropTypes.func,
        viewTTIstop: PropTypes.func,
        setAnalyticsPageName: PropTypes.func
    }).isRequired,
    locale: PropTypes.string.isRequired,
    setLegalName: PropTypes.func.isRequired,
    legalNameLabel: PropTypes.string.isRequired,
    legalNameTooltip: PropTypes.string.isRequired,
    legalName: PropTypes.string.isRequired,
    legalNameValidation: PropTypes.shape(validationObjectProps).isRequired,
    competitorUrlLoading: PropTypes.bool.isRequired,
    websiteUrl: PropTypes.string.isRequired,
    setWebsiteUrl: PropTypes.func.isRequired,
    websiteUrlLabel: PropTypes.string.isRequired,
    websiteUrlValidation: PropTypes.shape(validationObjectProps).isRequired,
    isAAPV2: PropTypes.bool
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setPropertyName: propertyName => dispatch(JoinDetailsAction.setPropertyName(propertyName)),
    setLatinPropertyName: latinPropertyName => dispatch(
        JoinDetailsAction.setLatinPropertyName(latinPropertyName)
    ),
    setPropertyType: propertyType => dispatch(JoinDetailsAction.setPropertyType(propertyType)),
    setCurrency: currency => dispatch(JoinDetailsAction.setCurrency(currency)),
    setUnitChange: unitCount => dispatch(JoinDetailsAction.setUnitCount(unitCount)),
    setManagerInfo: managerInfo => dispatch(JoinDetailsAction.setManagerInfo(managerInfo)),
    setChainInfo: chainInfo => dispatch(JoinDetailsAction.setChainInfo(chainInfo)),
    fetchCurrencyOnCountryCode: countryCode => dispatch(
        JoinDetailsAction.fetchCurrencyOnCountryCode(countryCode)
    ),
    setLegalName: legalName => dispatch(
        JoinDetailsAction.setLegalName(legalName)
    ),
    setWebsiteUrl: websiteUrl => dispatch(JoinDetailsAction.setWebsiteUrl(websiteUrl))
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const {
        app, joinDetails, location, hydration
    } = state;
    const {
        propertyName, latinPropertyName, propertyType, currency, unitCount, managerInfo, chainInfo,
        propertyNameValidation, latinPropertyNameValidation,
        propertyTypeValidation, unitCountValidation, currencyValidation,
        currencyList, legalName, legalNameValidation, competitorUrlLoading,
        websiteUrl, websiteUrlValidation
    } = joinDetails;
    const {
        referrer
    } = app;

    const { hydrationComplete } = hydration;
    const { address } = location;
    const parsedAddress = addressFields(address, app.features.useThreeLetterCountryCode);
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    return {
        websiteUrl,
        websiteUrlLabel: translations.getTranslation('property.website.url.label'),
        websiteUrlValidation,
        competitorUrlLoading,
        legalNameLabel: translations.getTranslation('property.legal.name.label'),
        legalNameTooltip: translations.getTranslation('property.legal.name.tooltip'),
        legalName,
        legalNameValidation,
        buttonBackLabel: translations.getTranslation('Vrbo.cta.back'),
        title: translations.getTranslation('property.details.title'),
        propertyNameLabel: translations.getTranslation('property.nameLabel'),
        propertyNameToolTip1: translations.getTranslation('property.name.toolTip1'),
        propertyNameToolTip2: translations.getTranslation('property.name.toolTip2'),
        propertyNameToolTip3: translations.getTranslation('property.name.toolTip3'),
        propertyTypeLabel: translations.getTranslation('property.typeLabel'),
        roomsLabel: translations.getTranslation(getUnitCountLabel(propertyType)),
        roomsToolTip: translations.getTranslation('property.numberOfRooms.toolTip'),
        currencyLabel: translations.getTranslation('currency.label'),
        managerInfoLabel: translations.getTranslation('property.managerInfo'),
        managerToolTip1: translations.getTranslation('property.questions.worksWithChannelManager.toolTip1'),
        managerToolTip2: translations.getTranslation('property.questions.worksWithChannelManager.toolTip2'),
        chainInfoLabel: translations.getTranslation('property.chainInfo'),
        chainInfoToolTip: translations.getTranslation('property.questions.partOfChain.toolTip'),
        yesLabel: translations.getTranslation('property.yesLabel'),
        noLabel: translations.getTranslation('property.noLabel'),
        latinRomanTip: translations.getTranslation('property.latinRomanTip'),
        countryCode: parsedAddress.country,
        referrer,
        translations,
        propertyName,
        latinPropertyName,
        propertyNameValidation,
        latinPropertyNameValidation,
        propertyType,
        propertyTypeValidation,
        currency,
        currencyValidation,
        unitCount,
        unitCountValidation,
        managerInfo,
        chainInfo,
        currencyList,
        location,
        hydrationComplete,
        locale: app.locale.canonical
    };
};

export {
    validationObjectProps,
    propertyDetailsProps,
    mapDispatchToProps,
    mapStateToProps
};
